/**
 * TurnstileWidget (iter79)
 *
 * Cloudflare Turnstile invisible-ish CAPTCHA. Loads the script lazily,
 * renders into an ID'd div, and calls `onVerify(token)` when the user
 * passes. Falls back to a "skip" flow only if `REACT_APP_TURNSTILE_SITE_KEY`
 * is missing (dev only).
 */
import { useEffect, useRef, useState } from "react";

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js";
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

let scriptPromise = null;
function loadScript() {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (window.turnstile) return resolve();
    const s = document.createElement("script");
    s.src = SCRIPT_URL;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export default function TurnstileWidget({ onVerify, testId = "turnstile-widget" }) {
  const ref = useRef(null);
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    if (!SITE_KEY) return;
    let widgetId = null;
    loadScript().then(() => {
      if (!ref.current || !window.turnstile) return;
      widgetId = window.turnstile.render(ref.current, {
        sitekey: SITE_KEY,
        callback: (token) => onVerify && onVerify(token),
        "expired-callback": () => onVerify && onVerify(""),
        "error-callback": () => onVerify && onVerify(""),
      });
      setRendered(true);
    }).catch(() => setRendered(false));
    return () => {
      if (widgetId && window.turnstile) {
        try { window.turnstile.remove(widgetId); } catch { /* ignore */ }
      }
    };
  }, [onVerify]);

  if (!SITE_KEY) return null; // Dev fallback — backend also bypasses when secret is empty.
  return (
    <div className="my-3" data-testid={testId}>
      <div ref={ref} />
      {!rendered && (
        <div className="text-[10px] text-[#A3A3A3]">Loading anti-bot check…</div>
      )}
    </div>
  );
}
